import { InputPasswordProvider } from './contexts/input-password-context'
import type { IInputPasswordProps, InputPasswordTagType } from './types'

import { Children, isValidElement } from 'react'
import { HelperText } from '~/components/helper-text'
import { isDEV, logger } from '~/utils'
import {
  InputPasswordActions,
  InputPasswordButton,
  InputPasswordField,
  InputPasswordHelperText,
  InputPasswordIcon,
  InputPasswordLabel,
  InputPasswordTag,
} from './components'
import { useInputPasswordProps } from './hooks'
import * as Comp from './input-password.styles'
import { SCOPE_WARNING_MESSAGE } from './utils/const'

const InputPasswordRoot = ({
  children,
  size = 'md',
  full,
  disabled = false,
  readonly = false,
  hasError = false,
  errorText = 'Dados inválidos',
  value = '',
  onChangeText,
  testID = 'inputTest',
  ...props
}: IInputPasswordProps) => {
  const { isActive, setIsActive } = useInputPasswordProps()

  const renderInternalComponents = Children.map(children, child => {
    if (!isValidElement(child)) return

    if (
      child.type === InputPasswordLabel ||
      child.type === InputPasswordActions ||
      child.type === InputPasswordHelperText ||
      child.type === InputPasswordTag
    ) {
      return child
    }

    if (isDEV()) {
      logger.warning(SCOPE_WARNING_MESSAGE)
      return
    }
  })

  const componentProperties = {
    size,
    full,
    hasError,
    errorText,
    disabled,
    readonly,
    value,
    onChangeText,
    isActive,
    setIsActive,
    testID,
  } as const

  return (
    <InputPasswordProvider {...componentProperties}>
      <Comp.Root {...props} testID={testID}>
        {renderInternalComponents}
        {hasError && (
          <HelperText
            disabled={disabled}
            readonly={readonly}
            hasError={hasError}
            withIcon={true}
            iconName={'rdicon-warning-circle'}
            iconSize="tiny"
          >
            {errorText ?? 'Dados inválidos'}
          </HelperText>
        )}
      </Comp.Root>
    </InputPasswordProvider>
  )
}

export const InputPassword = {
  Root: InputPasswordRoot,
  Field: InputPasswordField,
  Label: InputPasswordLabel,
  Actions: InputPasswordActions,
  Icon: InputPasswordIcon,
  Button: InputPasswordButton,
  HelperText: InputPasswordHelperText,
  Tag: InputPasswordTag,
}

export type { IInputPasswordProps, InputPasswordTagType }
